import { useEffect, useState } from 'react';
import { BellRing, CalendarClock, Save, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import {
  ChronologyAnalysisResult,
  ChronologyEvent,
  createDeadlineFromChronologyEvent,
  saveChronology,
} from '@/services/expedienteChronologyService';

interface Props {
  analysis: ChronologyAnalysisResult;
  chatId?: string;
  fileName?: string;
  onClose: () => void;
}

function riskVariant(risk: string) {
  return risk === 'alto' || risk === 'critico' ? 'destructive' : 'secondary';
}

export function ChronologyReviewPanel({ analysis, chatId, fileName, onClose }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [events, setEvents] = useState<ChronologyEvent[]>(analysis.events || []);
  const [chronologyId, setChronologyId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [creatingDeadlineIndex, setCreatingDeadlineIndex] = useState<number | null>(null);

  useEffect(() => {
    setEvents(analysis.events || []);
    setChronologyId(null);
  }, [analysis]);

  const updateEvent = (index: number, patch: Partial<ChronologyEvent>) => {
    setEvents((prev) => prev.map((event, i) => (i === index ? { ...event, ...patch } : event)));
  };

  const updateDeadline = (index: number, key: string, value: unknown) => {
    setEvents((prev) => prev.map((event, i) => {
      if (i !== index || !event.proposedDeadline) return event;
      return {
        ...event,
        proposedDeadline: {
          ...event.proposedDeadline,
          [key]: value,
        },
      };
    }));
  };

  const ensureSaved = async () => {
    if (!user?.id) throw new Error('No hay usuario activo');
    if (chronologyId) return { id: chronologyId, events };

    setSaving(true);
    try {
      const saved = await saveChronology({
        userId: user.id,
        chatId,
        fileName,
        sourceType: chatId ? 'chat' : 'document',
        analysis: {
          ...analysis,
          events,
        },
      });
      setChronologyId(saved.id);
      setEvents(saved.events);
      toast({ title: 'Cronologia guardada' });
      return saved;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      await ensureSaved();
    } catch (error) {
      toast({
        title: 'No se pudo guardar la cronologia',
        description: error instanceof Error ? error.message : 'Intentalo de nuevo.',
        variant: 'destructive',
      });
    }
  };

  const handleCreateDeadline = async (index: number) => {
    if (!user?.id) return;
    setCreatingDeadlineIndex(index);
    try {
      const saved = await ensureSaved();
      const event = saved.events[index] || events[index];
      const deadline = await createDeadlineFromChronologyEvent({
        userId: user.id,
        chronologyId: saved.id,
        event,
      });
      if (deadline) {
        setEvents((prev) => prev.map((item, i) => (
          i === index ? { ...item, createdDeadlineId: deadline.id } : item
        )));
        toast({ title: 'Plazo creado', description: deadline.title });
      }
    } catch (error) {
      toast({
        title: 'No se pudo crear el plazo',
        description: error instanceof Error ? error.message : 'Revisa los datos propuestos.',
        variant: 'destructive',
      });
    } finally {
      setCreatingDeadlineIndex(null);
    }
  };

  return (
    <div className="mx-4 mb-3 rounded-lg border bg-background shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-primary" />
            <h3 className="font-medium truncate">{analysis.title}</h3>
            <Badge variant={riskVariant(analysis.riskLevel) as any}>{analysis.riskLevel}</Badge>
          </div>
          {analysis.summary && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{analysis.summary}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleSave} disabled={saving} className="gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button size="icon" variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <ScrollArea className="max-h-[420px]">
        <div className="divide-y">
          {analysis.warnings?.length > 0 && (
            <div className="px-4 py-3 text-xs text-amber-700 bg-amber-50">
              {analysis.warnings.join(' | ')}
            </div>
          )}

          {events.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              No se detectaron eventos fechados revisables.
            </p>
          ) : events.map((event, index) => (
            <div key={`${event.date}-${index}`} className="p-4 space-y-3">
              <div className="grid gap-3 sm:grid-cols-[150px_1fr_180px]">
                <Input
                  type="date"
                  value={event.date || ''}
                  onChange={(e) => updateEvent(index, { date: e.target.value || null })}
                />
                <Input
                  value={event.actor || ''}
                  onChange={(e) => updateEvent(index, { actor: e.target.value || null })}
                  placeholder="Actor"
                />
                <Badge variant="outline" className="justify-center">
                  {Math.round((event.confidence || 0.5) * 100)}% confianza
                </Badge>
              </div>

              <Textarea
                value={event.description}
                onChange={(e) => updateEvent(index, { description: e.target.value })}
                rows={2}
              />

              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                {event.eventType && <span>Tipo: {event.eventType}</span>}
                {event.sourceRef && <span>Fuente: {event.sourceRef}</span>}
                {event.riskTags?.map((tag) => <Badge key={tag} variant="secondary">{tag}</Badge>)}
              </div>

              {event.proposedDeadline && (
                <div className="rounded-md border bg-muted/25 p-3 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <BellRing className="h-4 w-4 text-primary" />
                      <p className="text-sm font-medium">Plazo propuesto</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleCreateDeadline(index)}
                      disabled={!!event.createdDeadlineId || creatingDeadlineIndex === index}
                    >
                      {event.createdDeadlineId ? 'Creado' : creatingDeadlineIndex === index ? 'Creando...' : 'Crear plazo'}
                    </Button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[1fr_160px_140px]">
                    <Input
                      value={event.proposedDeadline.title}
                      onChange={(e) => updateDeadline(index, 'title', e.target.value)}
                      placeholder="Titulo del plazo"
                    />
                    <Input
                      type="date"
                      value={event.proposedDeadline.dueDate || ''}
                      onChange={(e) => updateDeadline(index, 'dueDate', e.target.value)}
                    />
                    <Input
                      value={event.proposedDeadline.legalReference || ''}
                      onChange={(e) => updateDeadline(index, 'legalReference', e.target.value)}
                      placeholder="Base legal"
                    />
                  </div>
                  <Textarea
                    value={event.proposedDeadline.description || ''}
                    onChange={(e) => updateDeadline(index, 'description', e.target.value)}
                    rows={2}
                    placeholder="Descripcion del plazo"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
